import { Box, Button, Grid, Modal, Space } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { MainLayout } from "../../layouts/MainLayout";
import { ContactInfo } from "../../widgets/profile/ContactInfo";
import { CVBlock } from "../../widgets/profile/CVBlock";
import { Goals } from "../../widgets/profile/Goals";
import { Preferences } from "../../widgets/profile/Preferences";
import { Experience } from "../../widgets/profile/Experience";
import { Skills } from "../../widgets/profile/Skills";
import { Education } from "../../widgets/profile/Education";
import { Certifications } from "../../widgets/profile/Certifications";

import { useEffect, useState } from "react";
import { DeleteAccount } from "../../features/delete-account/DeleteAccount";
import useAuthStore from "../../store/useAuthStore";
import useProfileStore from "../../store/useProfileStore";
import { useTranslation } from "react-i18next";
import { deleteUserStatus } from "../../api/userApi";
import { Languages } from "../../widgets/profile/Languages";
import { Hobbies } from "../../widgets/profile/Hobbies";
import { ReusableOnboardingTour } from "../../utils/components/onboardingTour/OnboardingTourWrapper";
import { TourButton } from "../../utils/components/onboardingTour/TourButton";
import { useOnboardingProfileSteps } from "../../utils/onboardingTourSteps";
import { OnboardingTourFocusRevealProps } from "@gfazioli/mantine-onboarding-tour";
import { scrollToTop } from "../../utils/helpers";

export const Profile = () => {
  const laptop = useMediaQuery("(max-width: 1100px)");
  const mobile = useMediaQuery("(max-width: 820px)");
  const { access_token, isRefreshing } = useAuthStore();
  const { fetchProfile, profile, tourButtonShow, profileLanguage } =
    useProfileStore();
  const [passwordRequired, setPasswordRequired] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);

  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (
      profile?.display_preferences?.hide_tour_tooltip_on_profile !== null &&
      profile?.display_preferences?.hide_tour_tooltip_on_profile !== undefined
    )
      setStarted(!profile?.display_preferences?.hide_tour_tooltip_on_profile);
  }, [profile?.display_preferences?.hide_tour_tooltip_on_profile]);

  const focusRevealProps: OnboardingTourFocusRevealProps = {
    popoverProps: {
      arrowSize: 20,
      position: mobile ? "bottom" : "left",
    },
  };

  const steps = useOnboardingProfileSteps();

  useEffect(() => {
    if (!isRefreshing && access_token) {
      fetchProfile();
    }
  }, [access_token, profileLanguage]);

  const handleOpenDelete = async () => {
    setDeleteLoading(true);

    try {
      const data = await deleteUserStatus();
      setPasswordRequired(data.data.password_required);
      setModalOpen(true);
    } catch {
      console.log("Error fetching delete status");
    }

    setDeleteLoading(false);
  };

  return (
    <MainLayout>
      <ReusableOnboardingTour
        focusRevealProps={focusRevealProps}
        tour={steps}
        started={started}
        onOnboardingTourEnd={() => {
          tourButtonShow({ hide_tour_tooltip_on_profile: true });
          scrollToTop();
        }}
        maw={mobile ? 325 : 280}
        __size="xs"
      >
        <Box m={mobile ? 16 : 40}>
          <Grid mb={mobile ? 24 : 32} gutter={mobile ? 24 : 35}>
            <Grid.Col span={laptop ? 12 : 9}>
              <ContactInfo />
            </Grid.Col>
            <Grid.Col span={laptop ? 12 : 3}>
              <CVBlock />
            </Grid.Col>
          </Grid>
          <Goals />
          <Space h={mobile ? "24px" : "32px"} />
          <Preferences />
          <Space h={mobile ? "24px" : "32px"} />

          <Grid gutter={35}>
            <Grid.Col span={laptop ? 12 : 6}>
              <Education />
            </Grid.Col>
            <Grid.Col span={laptop ? 12 : 6}>
              <Certifications />
            </Grid.Col>
          </Grid>
          <Grid mb={24} mt={24} gutter={mobile ? 24 : 35}>
            <Grid.Col span={laptop ? 12 : 4}>
              <Languages />
            </Grid.Col>
            <Grid.Col span={laptop ? 12 : 8}>
              <Skills />
            </Grid.Col>
          </Grid>

          <Grid gutter={mobile ? 24 : 35}>
            <Grid.Col span={laptop ? 12 : 8}>
              <Experience />
            </Grid.Col>
            <Grid.Col span={laptop ? 12 : 4}>
              <Hobbies />
            </Grid.Col>
          </Grid>
          <Space h="20px" />

          <Button
            variant="filled"
            color="red"
            data-onboarding-tour-id="profile-delete"
            onClick={handleOpenDelete}
            loading={deleteLoading}
            className="red-btn"
          >
            {t("deleteAccount")}
          </Button>

          <Modal
            zIndex={301}
            opened={modalOpen}
            onClose={() => setModalOpen(false)}
            title={t("confirm_delete_account")}
            centered
          >
            <DeleteAccount passwordRequired={passwordRequired} />
          </Modal>
        </Box>
      </ReusableOnboardingTour>
      <TourButton
        onClick={() => {
          tourButtonShow({ hide_tour_tooltip_on_profile: false });
        }}
      />
    </MainLayout>
  );
};
